import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { CalendarDaysIcon, ClockIcon, MapPinIcon, VideoCameraIcon } from "@heroicons/react/24/solid"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import React, { useState } from "react"
import { twMerge } from "tailwind-merge"

import { Banner, Divider } from "../components/shared/Layout"
import GraphQLProvider from "../providers/GraphQLProvider"
import { formatPhone } from "../utils/utils"

import EventRegistrationPayNowSection from "./ManageEventRegistration/EventRegistrationPayNowSection"

dayjs.extend(utc)
dayjs.extend(timezone)

const DetailItem = ({ icon: Icon, children, iconClassName = "h-5 w-5", textClassName = "text-sm" }) => (
  <div className="flex items-start space-x-3">
    <Icon className={twMerge("shrink-0", iconClassName)} aria-hidden="true" />
    <span className={twMerge(textClassName)}>{children}</span>
  </div>
)

const CancelConfirmationModal = ({ isOpen, onClose, onConfirm, accentColor }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 transition-opacity duration-300 ease-in-out">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl transition-all duration-300 ease-in-out sm:my-8 sm:scale-100">
        <div className="flex items-start">
          <div className="mr-4 flex size-12 shrink-0 items-center justify-center rounded-full bg-red-light sm:size-10">
            <ExclamationTriangleIcon className="size-6 text-red" aria-hidden="true" />
          </div>
          <div className="mt-0 text-left">
            <h3 className="text-lg font-semibold leading-6 text-black" id="modal-title">
              Cancel Program Registration
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray">
                Are you sure you want to cancel your registration for this program? This action cannot be undone.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-80 sm:ml-3 sm:w-auto"
            style={{ backgroundColor: accentColor }}
            onClick={onConfirm}>
            Yes, cancel
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-black shadow-sm ring-1 ring-inset ring-gray-light hover:bg-gray-ultralight sm:mt-0 sm:w-auto"
            onClick={onClose}>
            Go back
          </button>
        </div>
      </div>
    </div>
  )
}

const paymentStatusLabel = (status) => {
  switch (status) {
    case "paid":
      return "Paid"
    case "free":
      return "Free"
    case "upcoming":
      return "Payment scheduled"
    case "failed":
      return "Payment failed"
    default:
      return "Unpaid"
  }
}

const formatAmount = (amountCents, currency = "USD") => {
  if (amountCents == null) return null
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amountCents / 100)
}

const PageContent = ({ eventSeriesRegistration }) => {
  const {
    series,
    sessions,
    eventType,
    practice,
    client,
    registration,
    isOwnerCancelled,
    token,
    discount,
    finalAmountCents
  } = eventSeriesRegistration

  const theme = practice?.theme || {}
  const accentColor = theme.colorAccent || "#DF0054"
  const linkStyle = { color: accentColor }

  const clientEmail = client?.email || practice?.user?.email || "[email address]"
  const imageUrl = series?.photoUrl || eventType?.photoUrl
  const isVirtual = series?.kind === "virtual" || eventType?.kind === "virtual"
  const location = series?.location || eventType?.location

  const initialIsGeneralCancelled = registration?.state === "cancelled"
  const [cancellationStatus, setCancellationStatus] = useState({
    isCancelled: initialIsGeneralCancelled || Boolean(isOwnerCancelled),
    message: "",
    isError: false
  })
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [registrationPaymentStatus, setRegistrationPaymentStatus] = useState(registration?.paymentStatus)

  const paymentPendingFromCheckout =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("payment_pending") === "1"

  const registrationForPayment = registration
    ? { ...registration, paymentStatus: registrationPaymentStatus || registration.paymentStatus }
    : null

  const handlePaymentSuccess = () => {
    setRegistrationPaymentStatus("paid")
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href)
      url.searchParams.delete("payment_pending")
      window.history.replaceState({}, "", url.toString())
    }
  }

  const handleOpenCancelModal = (event) => {
    event.preventDefault()
    setIsCancelModalOpen(true)
  }

  const handleCloseCancelModal = () => setIsCancelModalOpen(false)

  const handleConfirmCancel = async () => {
    setIsCancelModalOpen(false)
    if (cancellationStatus.isCancelled) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
      const response = await fetch(`/manage_event_series_registrations/${token}/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        }
      })
      const data = await response.json()

      if (response.ok && data.success) {
        setCancellationStatus({
          isCancelled: true,
          message: data.message || "Your program registration has been cancelled.",
          isError: false
        })
      } else {
        setCancellationStatus({
          message: data.error || "Failed to cancel program registration. Please try again.",
          isError: true,
          isCancelled: cancellationStatus.isCancelled
        })
      }
    } catch {
      setCancellationStatus({
        message: "An unexpected error occurred. Please try again.",
        isError: true,
        isCancelled: cancellationStatus.isCancelled
      })
    }
  }

  return (
    <div className="min-h-screen bg-gray-ultralight py-8">
      <div className="mx-auto max-w-3xl px-4 lg:px-8 sm:px-6">
        <div className="mb-8 text-center">
          {isOwnerCancelled ? (
            <>
              <ExclamationTriangleIcon className="mx-auto size-16 text-gray" />
              <h2 className="mt-4 text-3xl font-bold tracking-tight">Registration cancelled</h2>
              <p className="mt-2 text-sm">Your registration for this program has already been cancelled.</p>
            </>
          ) : cancellationStatus.isCancelled ? (
            <>
              <CheckCircleIcon className="mx-auto size-16" style={{ color: accentColor }} />
              <h2 className="mt-4 text-3xl font-bold tracking-tight">Registration cancelled</h2>
              <p className="mt-2 text-sm">{cancellationStatus.message || "This program registration is cancelled."}</p>
            </>
          ) : (
            <>
              <CheckCircleIcon className="mx-auto size-16" style={{ color: accentColor }} />
              <h2 className="mt-4 text-3xl font-bold tracking-tight">
                {registrationPaymentStatus === "paid" || registration?.paymentStatus === "paid"
                  ? "You're registered!"
                  : registrationForPayment?.amountCents > 0 &&
                    ["unpaid", "failed"].includes(registrationPaymentStatus || registration?.paymentStatus)
                  ? "You're registered — payment pending"
                  : "You're registered!"}
              </h2>
              <p className="mt-2 text-sm">
                An email confirmation has been sent to <span className="font-bold">{clientEmail}</span>.
              </p>
              {paymentPendingFromCheckout && (
                <Banner type="warning" className="mx-auto mt-4 max-w-md text-left">
                  Your registration was created, but payment could not be processed. Please add a card below to complete
                  payment.
                </Banner>
              )}
            </>
          )}
        </div>

        {!isOwnerCancelled && (
          <div className="mx-auto max-w-xl overflow-hidden rounded-lg bg-white p-8 shadow">
            <div className="px-0">
              <div className="mt-6 space-y-4">
                <div className="flex items-start space-x-4">
                  {imageUrl && <img src={imageUrl} alt={series?.name} className="size-24 rounded-xl" />}
                  <h3 className="left-6 pt-4 text-2xl font-semibold leading-7">{series?.name}</h3>
                </div>

                {series?.formattedScheduleSummary && (
                  <DetailItem icon={CalendarDaysIcon}>
                    <p className="font-bold">{series.formattedScheduleSummary}</p>
                    {series?.sessionCount && (
                      <p className="top-4 text-sm text-gray-dark">{series.sessionCount} sessions</p>
                    )}
                  </DetailItem>
                )}

                {isVirtual ? (
                  <DetailItem icon={VideoCameraIcon}>
                    <p className="font-bold">Virtual</p>
                    {location ? (
                      <p className="top-4 text-sm text-gray-dark">
                        <a
                          href={location}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:opacity-80"
                          style={linkStyle}>
                          {location}
                        </a>
                      </p>
                    ) : (
                      <p className="top-4 text-sm text-gray-dark">Link will be emailed to you</p>
                    )}
                  </DetailItem>
                ) : (
                  location && (
                    <DetailItem icon={MapPinIcon}>
                      <p className="font-bold">In-person</p>
                      <p className="top-4 text-sm text-gray-dark">{location}</p>
                    </DetailItem>
                  )
                )}

                {sessions?.length > 0 && (
                  <DetailItem icon={ClockIcon}>
                    <p className="font-bold">Session schedule</p>
                    <ul className="top-4 space-y-1 text-sm text-gray-dark">
                      {sessions.map((session) => (
                        <li key={session.id}>{session.formattedTime || session.shortFormattedTime}</li>
                      ))}
                    </ul>
                  </DetailItem>
                )}
              </div>
            </div>

            <Divider />

            <div className="px-0">
              <p className="text-sm font-semibold text-gray-dark">Payment</p>
              <p className="mt-1 text-lg font-medium">
                {paymentStatusLabel(registrationPaymentStatus || registration?.paymentStatus)}
              </p>
              {finalAmountCents != null && finalAmountCents > 0 && (
                <p className="mt-1 text-sm text-gray-dark">
                  Total: {formatAmount(finalAmountCents, series?.amountCurrency)}
                  {discount?.amountCents > 0 && (
                    <span className="ml-2">(includes {formatAmount(discount.amountCents)} discount)</span>
                  )}
                </p>
              )}
            </div>

            <Divider />

            <div className="px-0">
              <div className="flex items-center space-x-4">
                <img className="size-12 rounded-xl" src={practice.user.defaultPhotoUrl} alt={practice.user.name} />
                <div>
                  <p className="text-lg font-medium">{practice.user.name}</p>
                  {practice.user.phone && (
                    <p className="text-sm" style={linkStyle}>
                      {formatPhone(practice.user.phone)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Divider />

            {registrationForPayment && (
              <div className="px-0">
                <EventRegistrationPayNowSection
                  practice={practice}
                  client={client}
                  registration={registrationForPayment}
                  isSeries
                  accentColor={accentColor}
                  onPaymentSuccess={handlePaymentSuccess}
                />
              </div>
            )}

            <Divider />

            <div className="mt-8 flex flex-col items-start space-y-3 sm:flex-row sm:justify-center sm:space-x-4 sm:space-y-0">
              {cancellationStatus.isCancelled ? (
                <p className="text-sm font-semibold text-green">Program registration cancelled</p>
              ) : (
                <a
                  href="#"
                  onClick={handleOpenCancelModal}
                  className="text-sm font-semibold hover:opacity-80"
                  style={linkStyle}>
                  Cancel registration
                </a>
              )}
              <a href={practice.url} className="text-sm font-semibold hover:opacity-80" style={linkStyle}>
                Book another appointment
              </a>
            </div>

            {cancellationStatus.message && !isCancelModalOpen && (
              <div className={`mt-4 text-center text-sm ${cancellationStatus.isError ? "text-red" : "text-green"}`}>
                {cancellationStatus.message}
              </div>
            )}
          </div>
        )}
      </div>

      <CancelConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
        accentColor={accentColor}
      />
    </div>
  )
}

const ManageEventSeriesRegistration = ({ eventSeriesRegistration }) => (
  <GraphQLProvider>
    <PageContent eventSeriesRegistration={eventSeriesRegistration} />
  </GraphQLProvider>
)

export default ManageEventSeriesRegistration
